/**
 * Staff controls for a brand's embedded widget chat (brands build plan §5.4).
 *
 * The case timeline remains the transcript and the normal case composer remains the only human send path. This
 * module adds the session state around those existing surfaces; it never creates a second send endpoint.
 * Owner: web-chat-staff.
 */
import { Button, Chip, Switch, toast } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, MessageCircle } from 'lucide-react'
import { describeError, unwrap } from '../../lib/api/client'
import {
  type ChatAvailabilityOut,
  getAvailabilityApiV1WidgetStaffAvailabilityGet,
  listSessionsApiV1WidgetStaffSessionsGet,
  putAvailabilityApiV1WidgetStaffAvailabilityPut,
  releaseApiV1WidgetStaffSessionsSessionIdReleasePost,
  type StaffChatSessionOut,
  type StaffChatSessionsOut,
  takeoverApiV1WidgetStaffSessionsSessionIdTakeoverPost,
} from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { useMe } from '../auth/session'
import { useBrandScope } from '../brands/use-brand-scope'
import type { ThreadView } from './api'

export const staffChatKeys = {
  sessions: (caseId: string) => ['case', caseId, 'widget-chat'] as const,
  availability: ['widget-chat', 'availability'] as const,
}

/** The current session wins; when every chat is closed, show only the newest historical one. */
export function currentStaffChat(items: StaffChatSessionOut[] | undefined): StaffChatSessionOut | null {
  return items?.find((item) => item.state !== 'closed') ?? items?.[0] ?? null
}

/** Session metadata refetches on the case invalidation SSE key, with a small polling fallback while it is live. */
export function useCaseStaffChat(caseId: string, enabled: boolean) {
  return useQuery({
    queryKey: staffChatKeys.sessions(caseId),
    queryFn: () => unwrap(listSessionsApiV1WidgetStaffSessionsGet({ query: { case_id: caseId } })),
    enabled: enabled && Boolean(caseId),
    staleTime: 4_000,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data as StaffChatSessionsOut | undefined
      return data?.items.some((item) => item.state !== 'closed') ? 10_000 : false
    },
  })
}

export function staffChatForThread(items: StaffChatSessionOut[] | undefined, threadId: string): StaffChatSessionOut | null {
  return (
    items?.find((item) => item.customer_thread_id === threadId && item.state !== 'closed') ??
    items?.find((item) => item.customer_thread_id === threadId) ??
    null
  )
}

function replaceSession(rows: StaffChatSessionsOut | undefined, session: StaffChatSessionOut): StaffChatSessionsOut {
  if (!rows) return { items: [session] }
  return { items: rows.items.map((item) => (item.id === session.id ? session : item)) }
}

/** Header action for the newest live widget chat. Billing sees state in the pane but never gets a write control. */
export function StaffChatHeaderAction({
  session,
  caseId,
  caseNumber,
  canWrite,
  compact = false,
}: {
  session: StaffChatSessionOut | null
  caseId: string
  caseNumber: string
  canWrite: boolean
  compact?: boolean
}) {
  const queryClient = useQueryClient()
  const action = useMutation({
    mutationFn: async () => {
      if (!session) throw new Error('No live chat session')
      return session.state === 'handoff'
        ? unwrap(releaseApiV1WidgetStaffSessionsSessionIdReleasePost({ path: { session_id: session.id } }))
        : unwrap(takeoverApiV1WidgetStaffSessionsSessionIdTakeoverPost({ path: { session_id: session.id } }))
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData<StaffChatSessionsOut>(staffChatKeys.sessions(caseId), (rows) => replaceSession(rows, saved))
      await queryClient.invalidateQueries({ queryKey: ['case', caseNumber] })
      toast.success(saved.state === 'handoff' ? 'You took over the chat.' : 'Chat handed back to Riley.')
    },
    onError: (error) => toast.error(describeError(error, 'Chat ownership was not changed')),
  })
  if (!session || session.state === 'closed' || !canWrite) return null
  const releasing = session.state === 'handoff'
  const releaseBlocked = releasing && session.riley_mode === 'off'
  return (
    <Button
      size={compact ? 'sm' : undefined}
      variant={releasing ? 'outline' : 'primary'}
      pending={action.isPending}
      disabled={releaseBlocked}
      disabledReason={releaseBlocked ? 'Riley is off for this brand, so a person must keep the chat.' : undefined}
      onClick={() => action.mutate()}
    >
      <MessageCircle aria-hidden="true" />
      {releasing ? 'Hand back' : 'Take over'}
    </Button>
  )
}

function stateCopy(session: StaffChatSessionOut): { label: string; tone: 'success' | 'warning' | 'neutral' | 'riley' } {
  if (session.state === 'closed') return { label: 'Chat closed', tone: 'neutral' }
  if (session.state === 'handoff') return { label: 'Staff answering', tone: 'success' }
  if (session.riley_answering) return { label: 'Riley answering', tone: 'riley' }
  return { label: 'Waiting for staff', tone: 'warning' }
}

/** Status strip above the customer timeline. The turns immediately below it are the live transcript. */
export function LiveChatPane({ session }: { session: StaffChatSessionOut }) {
  const state = stateCopy(session)
  const visitor = session.visitor_name?.trim() || (session.identity_kind === 'anonymous' ? 'Anonymous visitor' : 'Visitor')
  return (
    <section
      aria-label="Live widget chat"
      data-slot="live-widget-chat"
      data-chat-state={session.state}
      className="rounded-md border border-primary/25 bg-primary-soft/40 px-3 py-2.5"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
          <MessageCircle className="size-4 text-primary" aria-hidden="true" /> Live chat · {visitor}
        </span>
        <Chip size="sm" tone={state.tone}>
          {state.label}
        </Chip>
        {session.staff_online ? (
          <Chip size="sm" tone="success">
            Staff available
          </Chip>
        ) : null}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Started {formatWhen(session.started_at, { style: 'absolute' })} · Transcript updates live
      </p>
      {session.identity_kind === 'anonymous' ? (
        <p className="mt-2 text-xs text-warning" role="status">
          This visitor is anonymous. Replies stay disabled until chat delivery can send without an email address.
        </p>
      ) : null}
    </section>
  )
}

/** The known engine gap is explicit; the web app must not invent a recipient for an anonymous visitor. */
export function AnonymousChatComposerDisabled() {
  return (
    <div
      role="note"
      data-slot="composer"
      data-blocked="true"
      className="rounded-md border border-warning/40 bg-warning-soft px-3 py-3 text-sm"
    >
      <p className="font-medium">Reply unavailable for this anonymous chat</p>
      <p className="mt-1 text-xs text-muted-foreground">
        The visitor has no verified email address, and chat delivery currently requires one recipient. Nothing can be sent from this
        composer yet.
      </p>
    </div>
  )
}

function chatAvailableForScope(me: ReturnType<typeof useMe>, selected: string): boolean {
  if (!me || me.role === 'billing' || !me.brands) return false
  return me.brands.some(
    (brand) => brand.status === 'active' && (selected === 'all' || selected === brand.slug) && brand.features?.includes('chat_widget'),
  )
}

/** Global staff availability, shown only when the current brand scope has an active widget brand. */
export function ChatAvailabilityMenuItem() {
  const me = useMe()
  const scope = useBrandScope()
  const queryClient = useQueryClient()
  const enabled = chatAvailableForScope(me, scope.selected)
  const availability = useQuery({
    queryKey: staffChatKeys.availability,
    queryFn: () => unwrap(getAvailabilityApiV1WidgetStaffAvailabilityGet()),
    enabled,
    staleTime: 30_000,
    retry: false,
  })
  const save = useMutation({
    mutationFn: (online: boolean) => {
      const until = online ? new Date(Date.now() + 8 * 60 * 60 * 1_000).toISOString() : null
      return unwrap(putAvailabilityApiV1WidgetStaffAvailabilityPut({ body: { until } }))
    },
    onSuccess: (saved) => queryClient.setQueryData<ChatAvailabilityOut>(staffChatKeys.availability, saved),
    onError: (error) => toast.error(describeError(error, 'Chat availability was not changed')),
  })
  if (!enabled) return null
  const unknown = availability.isError && !availability.data
  return (
    <div className="flex min-w-56 items-center gap-2 px-2 py-1.5 text-sm" data-slot="chat-availability">
      <Switch
        aria-label="Staff available"
        checked={availability.data?.online ?? false}
        disabled={availability.isPending || unknown || save.isPending}
        onCheckedChange={(checked) => save.mutate(Boolean(checked))}
      />
      <span className="min-w-0 flex-1">
        <span className="block font-medium">Staff available</span>
        <span className="block text-xs text-muted-foreground">
          {unknown
            ? 'Availability unavailable'
            : availability.data?.online && availability.data.until
              ? `Online until ${formatWhen(availability.data.until, { style: 'time' })}`
              : 'Offline for new chats'}
        </span>
      </span>
      {availability.isPending || save.isPending ? (
        <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
      ) : null}
    </div>
  )
}

/** Whether this customer thread is the currently selected widget session's transcript. */
export function isWidgetChatThread(thread: Pick<ThreadView, 'uuid'>, session: StaffChatSessionOut | null): boolean {
  return Boolean(session && session.customer_thread_id === thread.uuid)
}
